import { useRef, useState } from "react";
import { api } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Upload,
  Camera,
  Loader2,
  Check,
  Clock,
  X,
  AlertCircle,
  RefreshCw,
  FileText,
  Eye,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CFG = {
  not_uploaded: { icon: Upload, label: "Sin subir", className: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  pending: { icon: Clock, label: "En revisión", className: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { icon: Check, label: "Aprobado", className: "bg-green-50 text-green-700 border-green-200" },
  rejected: { icon: X, label: "Rechazado", className: "bg-red-50 text-red-700 border-red-200" },
  needs_info: { icon: AlertCircle, label: "Más info", className: "bg-blue-50 text-blue-700 border-blue-200" },
};

const ACCEPT_TYPES = "image/jpeg,image/png,image/webp,application/pdf";
const MAX_SIZE_MB = 5;

/**
 * DocumentCard
 * Props:
 *  - title (e.g. "INE o Pasaporte (frente)")
 *  - description (helper text)
 *  - icon (Lucide component)
 *  - docKey (string field name to send to backend, e.g. "government_id_front")
 *  - submitEndpoint (default "/auth/verification") — should accept {[docKey]: dataUrl}
 *  - imageType (string) for /upload/image
 *  - doc { url, status, rejection_reason } from server
 *  - onUploaded(updatedDocs) callback
 *  - allowCamera (bool) default true
 *  - required (bool) — adds 'Obligatorio' marker
 */
export default function DocumentCard({
  title,
  description,
  icon: Icon = FileText,
  docKey,
  submitEndpoint = "/auth/verification",
  imageType,
  doc,
  onUploaded,
  allowCamera = true,
  required = true,
  locked = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [precheckOpen, setPrecheckOpen] = useState(false);
  const [precheckResult, setPrecheckResult] = useState(null);
  const [pendingDataUrl, setPendingDataUrl] = useState(null);
  const [pendingCompress, setPendingCompress] = useState(true);
  const [precheckLoading, setPrecheckLoading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const status = doc?.status || "not_uploaded";
  const cfg = STATUS_CFG[status] || STATUS_CFG.not_uploaded;
  const StatusIcon = cfg.icon;

  const isPdf = doc?.url && typeof doc.url === "string" && doc.url.startsWith("data:application/pdf");

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const performUpload = async (dataUrl, compress) => {
    setUploading(true);
    try {
      const { data: imgResp } = await api.post("/upload/image", {
        image_data: dataUrl,
        image_type: imageType || docKey,
        compress,
      });
      const storedUrl = imgResp?.data || dataUrl;
      const { data: subResp } = await api.post(submitEndpoint, { [docKey]: storedUrl });
      toast.success("Documento subido");
      onUploaded?.(subResp);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(false);
      setPendingDataUrl(null);
      setPrecheckResult(null);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Archivo demasiado grande (máx. ${MAX_SIZE_MB} MB)`);
      return;
    }
    const accepted = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!accepted.includes(file.type)) {
      toast.error("Formato no válido. Usa JPG, PNG, WEBP o PDF.");
      return;
    }

    let dataUrl;
    try {
      dataUrl = await readFileAsDataUrl(file);
    } catch {
      toast.error("Error al leer el archivo");
      return;
    }
    const compress = file.type !== "application/pdf";

    // Skip AI precheck for PDFs (vision can't reliably parse them) and host docs (host_verification endpoint)
    const skipPrecheck = file.type === "application/pdf" || submitEndpoint !== "/auth/verification";
    if (skipPrecheck) {
      await performUpload(dataUrl, compress);
      return;
    }

    // Run vision precheck before committing
    setPrecheckLoading(true);
    setPendingDataUrl(dataUrl);
    setPendingCompress(compress);
    try {
      const { data } = await api.post("/auth/verification/precheck", {
        image_data: dataUrl,
        doc_type: docKey,
      });
      if (data?.passed) {
        // straight upload
        setPrecheckLoading(false);
        await performUpload(dataUrl, compress);
      } else {
        // show warning dialog
        setPrecheckResult(data);
        setPrecheckOpen(true);
        setPrecheckLoading(false);
      }
    } catch (err) {
      // soft-fail: skip precheck on error
      console.warn("precheck failed", err);
      setPrecheckLoading(false);
      await performUpload(dataUrl, compress);
    }
  };

  const trigger = (which) => {
    if (which === "camera") cameraRef.current?.click();
    else fileRef.current?.click();
  };

  return (
    <div
      className={`border rounded-xl p-4 transition ${
        status === "rejected"
          ? "border-red-200 bg-red-50/40"
          : status === "approved"
          ? "border-green-200 bg-green-50/40"
          : status === "pending"
          ? "border-amber-200 bg-amber-50/40"
          : status === "needs_info"
          ? "border-blue-200 bg-blue-50/40"
          : "border-neutral-200"
      }`}
      data-testid={`doc-card-${docKey}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-neutral-700" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-sm text-neutral-900">{title}</p>
              {!required && (
                <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                  Opcional
                </span>
              )}
            </div>
            {description && (
              <p className="text-xs text-neutral-500 mt-0.5">{description}</p>
            )}
          </div>
        </div>
        <Badge
          variant="outline"
          className={`${cfg.className} text-xs whitespace-nowrap`}
          data-testid={`doc-status-${docKey}`}
        >
          <StatusIcon className="w-3 h-3 mr-1" />
          {cfg.label}
        </Badge>
      </div>

      {/* Rejection reason */}
      {status === "rejected" && doc?.rejection_reason && (
        <div className="bg-white border border-red-200 rounded-lg p-2.5 mb-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs">
            <p className="font-semibold text-red-800">Motivo</p>
            <p className="text-red-700">{doc.rejection_reason}</p>
          </div>
        </div>
      )}

      {/* Preview */}
      {doc?.url && (
        <div className="mb-3">
          {isPdf ? (
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white border border-neutral-200 rounded-lg p-3 hover:bg-neutral-50"
              data-testid={`doc-pdf-${docKey}`}
            >
              <FileText className="w-5 h-5 text-neutral-700" />
              <span className="text-sm flex-1">Ver documento PDF</span>
              <Eye className="w-4 h-4 text-neutral-500" />
            </a>
          ) : (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="block w-full"
              data-testid={`doc-preview-${docKey}`}
            >
              <img
                src={doc.url}
                alt={title}
                className="w-full h-32 object-cover rounded-lg border border-neutral-200"
              />
            </button>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        {locked ? (
          <p className="text-xs text-neutral-500 italic" data-testid={`doc-locked-${docKey}`}>
            Documento enviado a revisión — no editable
          </p>
        ) : (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT_TYPES}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`doc-input-${docKey}`}
            />
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button
              type="button"
              size="sm"
              variant={doc?.url ? "outline" : "default"}
              onClick={() => trigger("file")}
              disabled={uploading || precheckLoading}
              className={doc?.url ? "" : "bg-[#111111] hover:bg-neutral-800 text-white"}
              data-testid={`doc-upload-${docKey}`}
            >
              {uploading || precheckLoading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : doc?.url ? (
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              ) : (
                <Upload className="w-3.5 h-3.5 mr-1.5" />
              )}
              {precheckLoading ? "Analizando…" : doc?.url ? "Reemplazar" : "Subir"}
            </Button>
            {allowCamera && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => trigger("camera")}
                disabled={uploading || precheckLoading}
                className="sm:hidden"
                data-testid={`doc-camera-${docKey}`}
              >
                <Camera className="w-3.5 h-3.5 mr-1.5" />
                Cámara
              </Button>
            )}
          </>
        )}
      </div>

      {/* Image preview modal (simple) */}
      {previewOpen && doc?.url && !isPdf && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewOpen(false)}
        >
          <img
            src={doc.url}
            alt={title}
            className="max-w-full max-h-full rounded-lg"
          />
          <button
            onClick={() => setPreviewOpen(false)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* AI Pre-check warning dialog */}
      <Dialog
        open={precheckOpen}
        onOpenChange={(o) => {
          if (!o) {
            setPrecheckOpen(false);
            setPrecheckResult(null);
            setPendingDataUrl(null);
          }
        }}
      >
        <DialogContent data-testid={`precheck-dialog-${docKey}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Posibles problemas con tu foto
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-neutral-600 flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-amber-500 mt-0.5" />
              Nuestra revisión automática encontró lo siguiente:
            </p>
            <ul className="space-y-2">
              {(precheckResult?.issues || []).map((iss, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg p-2.5"
                  data-testid={`precheck-issue-${i}`}
                >
                  <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  <span className="text-amber-900">{iss}</span>
                </li>
              ))}
            </ul>
            {/* Preview the rejected image */}
            {pendingDataUrl && pendingDataUrl.startsWith("data:image") && (
              <img
                src={pendingDataUrl}
                alt="Vista previa"
                className="w-full max-h-44 object-contain rounded-lg border border-neutral-200 bg-neutral-50"
              />
            )}
            <p className="text-xs text-neutral-500">
              Te recomendamos volver a tomar la foto. Si crees que la foto es correcta,
              puedes subirla de todos modos.
            </p>
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setPrecheckOpen(false);
                setPrecheckResult(null);
                setPendingDataUrl(null);
                trigger("file");
              }}
              data-testid={`precheck-retake-${docKey}`}
            >
              <Camera className="w-3.5 h-3.5 mr-1.5" />
              Volver a tomar
            </Button>
            <Button
              className="bg-[#111111] hover:bg-neutral-800 text-white"
              onClick={async () => {
                setPrecheckOpen(false);
                if (pendingDataUrl) {
                  await performUpload(pendingDataUrl, pendingCompress);
                }
              }}
              data-testid={`precheck-force-upload-${docKey}`}
            >
              Subir de todos modos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
